import type { LDClient, LDContext, LDFlagSet } from "launchdarkly-js-client-sdk"
import { initialize } from "launchdarkly-js-client-sdk"
import { debugLogInDev } from "../util/util"
import type { Config } from "@ironzion/frontend-js-config"

let ldClient: LDClient

type Flags = [string, boolean][]

type GotFlagsCallback = (flags: Flags) => void

// https://launchdarkly.github.io/js-client-sdk/interfaces/_launchdarkly_js_client_sdk_.ldflagset.html
// We specifically only care if the flag is `true`
function prepareFlags(allFlags: LDFlagSet): Flags {
  const flags: Flags = []
  for (const key in allFlags) {
    if (allFlags[key] === true) {
      flags.push([key, true])
    } else {
      flags.push([key, false])
    }
  }
  return flags
}

/**
 *  LD returns an object for .allFlags() that is `{ key: any }`
 *
 *  https://launchdarkly.github.io/js-client-sdk/interfaces/_launchdarkly_js_client_sdk_.ldclient.html#allflags
 */
const refreshFlagsCallback = (client: LDClient, cb: GotFlagsCallback, logError: (error: Error) => void): (() => void) =>
  function () {
    try {
      const allFlags = client.allFlags()
      debugLogInDev(`Successfully loaded ${Object.keys(allFlags).length} feature flags`)
      const preparedFlags = prepareFlags(allFlags)
      cb(preparedFlags)
    } catch (error) {
      if (logError) {
        logError(error)
      }

      throw error
    }
  }

const initializeListeners = (client: LDClient, cb: GotFlagsCallback, logError: (error: Error) => void): void => {
  client.on("ready", refreshFlagsCallback(client, cb, logError))
  client.on("change", refreshFlagsCallback(client, cb, logError))
}

export const refreshFeatureFlags = async (
  config: Config,
  user: LDContext,
  cb: GotFlagsCallback,
  logError: (error: Error) => void,
): Promise<void> => {
  // Init with user if we have them
  if (!ldClient && user) {
    debugLogInDev("Initialize LaunchDarkly with user")
    ldClient = initialize(config.launchDarklyKey, user, {
      bootstrap: defaultFeatureFlags,
    })

    initializeListeners(ldClient, cb, logError)

    return
  }

  // Init anonymous if we don't
  if (!ldClient && !user) {
    debugLogInDev("Initialize LaunchDarkly with anonymous context")
    ldClient = initialize(config.launchDarklyKey, {
      anonymous: true,
      bootstrap: defaultFeatureFlags,
    })
    initializeListeners(ldClient, cb, logError)
  }

  /* ldClient definitely initialized now */

  if (user) {
    debugLogInDev("Refreshing flags!")
    // https://docs.launchdarkly.com/sdk/features/identify
    await ldClient.identify(user, undefined, refreshFlagsCallback(ldClient, cb, logError))
  }
}

/**
 * Subset of feature flags that are important to load before rendering certain UI.
 *
 * This largely only affects a pre-authenticated state, because by the time we've had a chance to
 * make requests to the GQL API, we'll have already waited for LD to load the available flags. But
 * prior to that point we don't block rendering the UI on LD.
 */
const defaultFeatureFlags = {
  "freemium-account-creation": true,
  /**
   * If users block LaunchDarkly in the browser, we want to make sure
   * they don't suddenly see some experience we haven't specifically
   * built around for their org tier. Right now, VendrFree tier
   * users see outdated UI if LD fails to load or is blocked
   */
  "premium-addon-based-experience": true,
}
